import React, { useEffect, useRef, useState } from 'react';
import takePutMugSprite from '../assets/take_put_mug_spritesheet.png';
import { MugPlayRequest, HalfRequest } from '../hooks/useMugAnimationController';
import { useShellLayout } from '../hooks/useShellLayout';
import { getMugOverlayStyle } from './mugOverlay';

interface TakePutMugAnimationProps {
    request: MugPlayRequest;
    onDone?: () => void;
    /** Called when a half finishes its animation - triggers next in queue */
    onHalfComplete?: (half: 'top' | 'bottom') => void;
    /** Called when a half is 1 frame from ending (for precise StaticMug sync) */
    onFrameNearEnd?: (half: 'top' | 'bottom', direction: 'forward' | 'reverse') => void;
}

// === SPRITESHEET SETTINGS ===
const FRAME_WIDTH = 480;
const FRAME_HEIGHT = 847;
const COLS = 10;
const TOTAL_FRAMES = 95;
const FPS = 30;
const FADE_PERCENTAGE = 0.4;
const VIGNETTE_SIZE = 40;

/** State for one half of the animation */
interface HalfState {
    isPlaying: boolean;
    frame: number;
    direction: 'forward' | 'reverse';
    lastRequestKey: number | null;
}

const createInitialHalfState = (): HalfState => ({
    isPlaying: false,
    frame: 0,
    direction: 'forward',
    lastRequestKey: null,
});

/**
 * Two-channel TakePutMug animation component.
 * Top and bottom halves are independent and can animate simultaneously.
 */
const TakePutMugAnimation: React.FC<TakePutMugAnimationProps> = ({ request, onDone, onHalfComplete, onFrameNearEnd }) => {
    const { isMobileShell } = useShellLayout();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const spriteRef = useRef<HTMLImageElement | null>(null);

    // Independent state for each half
    const topStateRef = useRef<HalfState>(createInitialHalfState());
    const bottomStateRef = useRef<HalfState>(createInitialHalfState());

    // Force re-render when animation state changes
    const [renderTick, setRenderTick] = useState(0);

    // Track if we've fired onFrameNearEnd for current animation cycle
    const topNearEndFiredRef = useRef(false);
    const bottomNearEndFiredRef = useRef(false);

    // Load sprite
    useEffect(() => {
        const img = new Image();
        img.src = takePutMugSprite;
        img.onload = () => {
            spriteRef.current = img;
        };
    }, []);

    // Process incoming requests for each half
    useEffect(() => {
        let changed = false;

        if (request.top && topStateRef.current.lastRequestKey !== request.top.requestKey) {
            console.log('[TakePutMugAnimation] Starting TOP half:', request.top.direction);
            topStateRef.current = {
                isPlaying: true,
                frame: request.top.direction === 'reverse' ? TOTAL_FRAMES - 1 : 0,
                direction: request.top.direction,
                lastRequestKey: request.top.requestKey,
            };
            topNearEndFiredRef.current = false;
            changed = true;
        }

        // Check bottom request
        if (request.bottom && bottomStateRef.current.lastRequestKey !== request.bottom.requestKey) {
            console.log('[TakePutMugAnimation] Starting BOTTOM half:', request.bottom.direction);
            bottomStateRef.current = {
                isPlaying: true,
                frame: request.bottom.direction === 'reverse' ? TOTAL_FRAMES - 1 : 0,
                direction: request.bottom.direction,
                lastRequestKey: request.bottom.requestKey,
            };
            bottomNearEndFiredRef.current = false;
            changed = true;
        }

        if (changed) {
            setRenderTick(prev => prev + 1);
        }
    }, [request.top?.requestKey, request.bottom?.requestKey]);

    // Animation Loop - runs when either half is playing
    useEffect(() => {
        const isAnyPlaying = topStateRef.current.isPlaying || bottomStateRef.current.isPlaying;
        if (!isAnyPlaying || !spriteRef.current || !canvasRef.current) return;

        let animationId: number;
        let lastTime = 0;
        const interval = 1000 / FPS;
        const fadeFrames = TOTAL_FRAMES * FADE_PERCENTAGE;

        const render = (time: number) => {
            if (time - lastTime > interval) {
                lastTime = time;
                const ctx = canvasRef.current?.getContext('2d');
                if (!ctx || !spriteRef.current) {
                    animationId = requestAnimationFrame(render);
                    return;
                }

                const canvasW = ctx.canvas.width;
                const canvasH = ctx.canvas.height;

                // Clear entire canvas
                ctx.clearRect(0, 0, canvasW, canvasH);

                let anyStillPlaying = false;

                // Render TOP half
                if (topStateRef.current.isPlaying) {
                    const state = topStateRef.current;
                    const isComplete = state.direction === 'reverse'
                        ? state.frame < 0
                        : state.frame >= TOTAL_FRAMES;

                    // Fire near-end callback 1 frame before completion
                    const isNearEnd = state.direction === 'reverse'
                        ? state.frame === 0
                        : state.frame === TOTAL_FRAMES - 1;
                    if (isNearEnd && !topNearEndFiredRef.current) {
                        topNearEndFiredRef.current = true;
                        onFrameNearEnd?.('top', state.direction);
                    }

                    if (isComplete) {
                        topStateRef.current.isPlaying = false;
                        onHalfComplete?.('top');
                    } else {
                        anyStillPlaying = true;

                        const col = state.frame % COLS;
                        const row = Math.floor(state.frame / COLS);
                        const sx = col * FRAME_WIDTH;
                        const sy = row * FRAME_HEIGHT;

                        // Calculate opacity based on frame progress (0 -> 1 in first 40% of frames)
                        // This handles both fade-in (forward start) and fade-out (reverse end) automatically
                        const alpha = Math.max(0, Math.min(1, state.frame / fadeFrames));

                        ctx.save();
                        ctx.globalAlpha = alpha;
                        ctx.beginPath();
                        ctx.rect(0, 0, canvasW, canvasH / 2);
                        ctx.clip();
                        ctx.drawImage(
                            spriteRef.current,
                            sx, sy, FRAME_WIDTH, FRAME_HEIGHT,
                            0, 0, canvasW, canvasH
                        );
                        ctx.restore();

                        // Advance frame
                        topStateRef.current.frame += state.direction === 'reverse' ? -1 : 1;
                    }
                }

                // Render BOTTOM half
                if (bottomStateRef.current.isPlaying) {
                    const state = bottomStateRef.current;
                    const isComplete = state.direction === 'reverse'
                        ? state.frame < 0
                        : state.frame >= TOTAL_FRAMES;

                    // Fire near-end callback 1 frame before completion
                    const isNearEnd = state.direction === 'reverse'
                        ? state.frame === 0
                        : state.frame === TOTAL_FRAMES - 1;
                    if (isNearEnd && !bottomNearEndFiredRef.current) {
                        bottomNearEndFiredRef.current = true;
                        onFrameNearEnd?.('bottom', state.direction);
                    }

                    if (isComplete) {
                        bottomStateRef.current.isPlaying = false;
                        onHalfComplete?.('bottom');
                    } else {
                        anyStillPlaying = true;

                        const col = state.frame % COLS;
                        const row = Math.floor(state.frame / COLS);
                        const sx = col * FRAME_WIDTH;
                        const sy = row * FRAME_HEIGHT;

                        // Calculate opacity based on frame progress
                        const alpha = Math.max(0, Math.min(1, state.frame / fadeFrames));

                        ctx.save();
                        ctx.globalAlpha = alpha;
                        ctx.beginPath();
                        ctx.rect(0, canvasH / 2, canvasW, canvasH / 2);
                        ctx.clip();
                        ctx.drawImage(
                            spriteRef.current,
                            sx, sy, FRAME_WIDTH, FRAME_HEIGHT,
                            0, 0, canvasW, canvasH
                        );
                        ctx.restore();

                        // Advance frame
                        bottomStateRef.current.frame += state.direction === 'reverse' ? -1 : 1;
                    }
                }

                // Apply Soft Frame Vignette (Fade edges to transparent)
                if (anyStillPlaying) {
                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-out';

                    // Left Edge
                    const gradL = ctx.createLinearGradient(0, 0, VIGNETTE_SIZE, 0);
                    gradL.addColorStop(0, 'black'); // Removes content (Opaque destination-out)
                    gradL.addColorStop(1, 'transparent'); // Keeps content
                    ctx.fillStyle = gradL;
                    ctx.fillRect(0, 0, VIGNETTE_SIZE, canvasH);

                    // Right Edge
                    const gradR = ctx.createLinearGradient(canvasW, 0, canvasW - VIGNETTE_SIZE, 0);
                    gradR.addColorStop(0, 'black');
                    gradR.addColorStop(1, 'transparent');
                    ctx.fillStyle = gradR;
                    ctx.fillRect(canvasW - VIGNETTE_SIZE, 0, VIGNETTE_SIZE, canvasH);

                    // Top Edge
                    const gradT = ctx.createLinearGradient(0, 0, 0, VIGNETTE_SIZE);
                    gradT.addColorStop(0, 'black');
                    gradT.addColorStop(1, 'transparent');
                    ctx.fillStyle = gradT;
                    ctx.fillRect(0, 0, canvasW, VIGNETTE_SIZE);

                    // Bottom Edge
                    const gradB = ctx.createLinearGradient(0, canvasH, 0, canvasH - VIGNETTE_SIZE);
                    gradB.addColorStop(0, 'black');
                    gradB.addColorStop(1, 'transparent');
                    ctx.fillStyle = gradB;
                    ctx.fillRect(0, canvasH - VIGNETTE_SIZE, canvasW, VIGNETTE_SIZE);

                    ctx.restore();
                }

                if (!anyStillPlaying) {
                    onDone?.();
                    setRenderTick(prev => prev + 1); // Force re-render to hide canvas
                    return;
                }
            }
            animationId = requestAnimationFrame(render);
        };

        animationId = requestAnimationFrame(render);

        return () => cancelAnimationFrame(animationId);
    }, [renderTick, onDone]);

    // Hide canvas when nothing is playing
    const isAnyPlaying = topStateRef.current.isPlaying || bottomStateRef.current.isPlaying;
    if (!isAnyPlaying) return null;

    return (
        <canvas
            ref={canvasRef}
            width={FRAME_WIDTH}
            height={FRAME_HEIGHT}
            className="fixed z-40 pointer-events-none"
            style={getMugOverlayStyle(isMobileShell)}
        />
    );
};

export default TakePutMugAnimation;
